/**
 * The rule behind every grouped multi-filter in IRES Office (the `MultiSelectionFilterMenu` with
 * group headings): inside a group an item needs ANY ticked option, across groups ALL groups that
 * have one ticked; a group with nothing ticked does not filter (decision was made on 2026-10-01
 * (Taimoor)). A list only supplies its groups and how one option matches one item.
 *
 * Used by the admin portal's Mandanten and Geräte lists; generic so the next list plugs in.
 */

/**
 * Whether an item passes a grouped selection.
 *
 * @param item - the row to test.
 * @param selection - the ticked options.
 * @param groupOf - an option's group; an option with no group (unknown) is ignored.
 * @param matchesOption - whether one ticked option matches the item on its own.
 * @returns true when every group with a ticked option has at least one that matches.
 */
export function matchesGroupedSelection<Item, Option, Group>(
  item: Item,
  selection: Option[],
  groupOf: (option: Option) => Group | null,
  matchesOption: (item: Item, option: Option) => boolean
): boolean {
  const byGroup = new Map<Group, Option[]>();

  for (const option of selection) {
    const group = groupOf(option);
    if (group !== null) {
      byGroup.set(group, [...(byGroup.get(group) ?? []), option]);
    }
  }

  return Array.from(byGroup.values()).every((options) => options.some((option) => matchesOption(item, option)));
}

/**
 * The ticked options in menu order, whatever order they were ticked in: what a list header names in
 * brackets ("Mandanten: 128 (Kunden, Aktiv)").
 *
 * @param selection - the ticked options.
 * @param menuOrder - every option in the order the menu shows them.
 * @returns the ticked ones, in menu order.
 */
export function orderedSelection<Option>(selection: Option[], menuOrder: Option[]): Option[] {
  return menuOrder.filter((option) => selection.includes(option));
}
